import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/calendar/ui/dialog'
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/calendar/ui/form'
import { Input } from '@/components/calendar/ui/input'
import { Button } from '@/components/calendar/ui/button'
import { DateTimePicker } from '@/components/calendar/ui/date-time-picker'
import { FC, PropsWithChildren } from 'react'
import { Combobox } from '@/components/calendar/ui/combobox'
import { createVehicleClassPrice, createVehiclePrice } from '@/rental/services/vehicle_service'
import { toast } from 'sonner'

const formSchema = z
    .object({
        vehicleId: z.number(),
        start: z.string().datetime(),
        end: z.string().datetime(),
        price: z.string().min(0, 'Price must be greater than 0'),
    })
    .refine(
        (data) => {
            const start = new Date(data.start)
            const end = new Date(data.end)
            return end >= start
        },
        {
            message: 'End time must be after start time',
            path: ['end'],
        }
    )

interface Props extends PropsWithChildren {
    newEventDialogOpen: boolean;
    setNewEventDialogOpen: (open: boolean) => void;
    categories: any[];
    currency: string;
    onSaved?: () => void;
}

const Create: FC<Props> = ({ newEventDialogOpen, setNewEventDialogOpen, categories, currency, onSaved }) => {

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            vehicleId: undefined,
            start: '',
            end: '',
            price: '',
        },
    })

    async function onSubmit(values: z.infer<typeof formSchema>) {
        try {
            await createVehiclePrice(values);
            setNewEventDialogOpen(false)
            form.reset()
            onSaved?.();

        } catch (error: any) {
            console.error("Error creating vehicle class price:", error);
            toast.error(error?.response?.data?.message || "Failed to create seasonal rate. Please try again.");
        }
    }

    return (
        <Dialog open={newEventDialogOpen} onOpenChange={setNewEventDialogOpen} modal={false}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>New seasonal rate</DialogTitle>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">

                        <FormField
                            control={form.control}
                            name="vehicleId"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="font-bold">Vehicle *</FormLabel>
                                    <FormControl>
                                        <Combobox field={field} options={categories} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="price"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="font-bold">Price({currency}) *</FormLabel>
                                    <FormControl>
                                        <Input type='number' min={0} required placeholder="set a price" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="start"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="font-bold">Start *</FormLabel>
                                    <FormControl>
                                        <DateTimePicker field={field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="end"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="font-bold">End *</FormLabel>
                                    <FormControl>
                                        <DateTimePicker field={field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <div className="flex justify-end">
                            <Button type="submit">Create event</Button>
                        </div>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}

export default Create;